import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';

const defaultWidth = 84;
const defaultHeight = 86;
const defaultColor = '#FF0083';
const defaultBackgroundColor = '#ffffff';
const pixelBorder = 3;

const PerionLoader = ({
    isLogoVisible = true,
    color = defaultColor,
    backgroundColor = defaultBackgroundColor,
    maskColor = 'rgba(255, 255, 255, 0.7)',
    overlay = true,
    size = 'medium' // 'small', 'medium', 'large', or number for custom size
}) => {
    const containerRef = useRef(null);
    const [dimensions, setDimensions] = useState({width: 0, height: 0});

    // Size presets
    const sizeMap = {
        small: 40,
        medium: 84,
        large: 120
    };

    const loaderSize = typeof size === 'number' ? size : sizeMap[size] || sizeMap.medium;

    useEffect(() => {
        if (!overlay) return;

        const updateDimensions = () => {
            if (containerRef.current && containerRef.current.parentElement) {
                const parent = containerRef.current.parentElement;
                const computedStyle = window.getComputedStyle(parent);

                const width = parent.offsetWidth
                    + (parseFloat(computedStyle.marginLeft) || 0)
                    + (parseFloat(computedStyle.marginRight) || 0);

                const height = parent.offsetHeight
                    + (parseFloat(computedStyle.marginTop) || 0)
                    + (parseFloat(computedStyle.marginBottom) || 0);

                setDimensions({width, height});
            }
        };

        updateDimensions();

        const resizeObserver = new ResizeObserver(updateDimensions);
        if (containerRef.current && containerRef.current.parentElement) {
            resizeObserver.observe(containerRef.current.parentElement);
        }

        window.addEventListener('resize', updateDimensions);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateDimensions);
        };
    }, [overlay]);

    const svgHTML = (color, backgroundColor, width, height) => (
        <svg width={width} height={height} viewBox="0 0 84 86" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="42" cy="42" r="38.5" fill={color}/>
            <g clipPath="url(#clip0_256_82488)">
                <path d="M 38.6872 38.6678 C 42.9106 33.5371 43.125 26.7359 39.166 23.477 C 35.207 20.2181 28.5738 21.7355 24.3503 26.8662 C 20.1269 31.997 19.9125 38.7981 23.8716 42.0571 C 27.8306 45.316 34.4638 43.7986 38.6872 38.6678 Z" fill={backgroundColor}/>
                <path d="M 39.8669 62.2967 C 41.7131 60.0539 39.0035 54.7733 33.8149 50.5022 C 28.6262 46.2311 22.9234 44.5869 21.0772 46.8297 C 19.231 49.0725 21.9406 54.353 27.1292 58.6241 C 32.3178 62.8952 38.0207 64.5395 39.8669 62.2967 Z" fill={backgroundColor}/>
                <path d="M 59.6481 57.9257 C 63.8716 52.7949 64.0859 45.9938 60.1269 42.7348 C 56.1679 39.4759 49.5347 40.9933 45.3113 46.124 C 41.0878 51.2548 40.8735 58.0559 44.8325 61.3149 C 48.7915 64.5738 55.4247 63.0564 59.6481 57.9257 Z" fill={backgroundColor}/>
                <path d="M 62.9216 37.9764 C 64.7678 35.7336 62.0582 30.453 56.8696 26.1819 C 51.6809 21.9108 45.9781 20.2666 44.1319 22.5094 C 42.2857 24.7522 44.9953 30.0327 50.1839 34.3038 C 55.3725 38.5749 61.0754 40.2192 62.9216 37.9764 Z" fill={backgroundColor}/>
            </g>
            <defs>
                <clipPath id="clip0_256_82488">
                    <rect width="44" height="44" fill={backgroundColor} transform="translate(20 21)"/>
                </clipPath>
            </defs>
        </svg>
    );

    const calculateLoaderSize = () => {
        if (!overlay) return loaderSize;

        const smallestDimension = dimensions.height > dimensions.width
            ? dimensions.width
            : dimensions.height;

        const diagonal = smallestDimension / Math.sqrt(2);
        const maxDiagonal = Math.sqrt(defaultWidth * defaultWidth + defaultHeight * defaultHeight);

        return diagonal > maxDiagonal ? maxDiagonal : diagonal;
    };

    const actualSize = calculateLoaderSize();

    const containerStyle = overlay ? {
        position: 'absolute',
        zIndex: 9999,
        left: 0,
        top: 0,
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`,
        backgroundColor: maskColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    } : {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
    };

    const spinnerStyle = {
        position: 'relative',
        width: `${actualSize}px`,
        height: `${actualSize}px`
    };

    const borderStyle = {
        position: 'absolute',
        width: '100%',
        height: '100%',
        border: `${pixelBorder}px solid transparent`,
        borderTop: `${pixelBorder}px solid ${color}`,
        borderRadius: '50%',
        boxSizing: 'content-box',
        animation: 'perion-loader-spin 2s linear infinite'
    };

    const svgContainerStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: `${actualSize}px`,
        height: `${actualSize}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    };

    return (
        <div ref={containerRef} className="perion-loading__container" style={containerStyle}>
            <div className="perion-loading__spinner" style={spinnerStyle}>
                <div className="perion-loading__border" style={borderStyle} />
                {isLogoVisible && (
                    <div style={svgContainerStyle}>
                        {svgHTML(color, backgroundColor, actualSize, actualSize)}
                    </div>
                )}
            </div>
        </div>
    );
};

PerionLoader.propTypes = {
    isLogoVisible: PropTypes.bool,
    color: PropTypes.string,
    backgroundColor: PropTypes.string,
    maskColor: PropTypes.string,
    overlay: PropTypes.bool,
    size: PropTypes.oneOfType([
        PropTypes.oneOf(['small', 'medium', 'large']),
        PropTypes.number
    ])
};

export default PerionLoader;

